import React, { useState } from 'react'
import { Formik, Form } from 'formik'
import { Button } from 'react-bootstrap'
import {
  DISPLAY_MODE_STANDARD,
  GATED_MODE_EMAIL_GATE,
  GATED_MODE_PASSWORD,
  GATED_MODE_STANDARD,
  GATED_MODE_EMAIL_WHITELIST,
  GATED_MODE_SSO,
  LABEL_MAP,
  ACTION_UPDATE,
  ACTION_CREATE
} from '../interface'
import { isPDF } from 'utils/general'
import DomainForm from './components/DomainForm'
import FileSelectionForm from './components/FileSelectionForm'
import ToggleButtonsForm from './components/ToggleButtonsForm'
import ActionBar from '../../ActionBar'
import { getDomainSuffix } from 'utils/general'
import { PRO_MAX_FILE_SIZES } from 'constants/plans'
import { getFileType } from 'helpers/file-type'
import { ACCOUNT_STATUS } from '../../../constants'

const CreateUpdateSiteForm = ({
  formValues,
  onSubmit,
  validate,
  onConvertToWebsite,
  onShowCodeEditor,
  onOpenTextEditor,
  onOpenUrl,
  onOpenGithubModal,
  onOpenTransferRequestLinkModal,
  onOpenDropBox,
  viewRepos
}) => {
  const { action, userProfile, site, files, customDomains, tlds, disableProFeatures, draggedFile, currentFormValues } =
    formValues
  const { productId } = userProfile

  const {
    subdomain = '',
    passwordProtected = false,
    password = '',
    fileUploadLimit = PRO_MAX_FILE_SIZES[productId],
    disableIndexing = false,
    disableExportPdf = false,
    siteConfiguration = false,
    displayMode = DISPLAY_MODE_STANDARD,
    enableEmailGate = false,
    enableEmailWhitelist = false,
    allowedEmails = [],
    enableSSO = false,
    feedbackMode = false,
    filename = '' /** v1 */,
    inPreviewMode,
    pdfPageAnalytics = false
  } = site || {}

  const [domainError, setDomainError] = useState(null)

  const updatingPhpFile = site?.type?.includes('php') && action === ACTION_UPDATE

  const domainFormValues = {
    domain: subdomain,
    subdomain: subdomain?.split('.')[0] || '',
    domainSuffix: getDomainSuffix(subdomain).tld || tlds[0],
    customDomains: customDomains,
    tlds
  }

  const uploadFormValues = {
    selectedFile: files?.[0],
    draggedFile: draggedFile,
    addedFiles: files?.length >= 1 ? files.slice(1) : [],
    fileUploadLimit,
    progress: 0,
    indexFile: files.find((file) => file.isIndex === true || file.name === 'index.html')?.name,
    fileType: getFileType(files?.[0]?.fileName || filename || draggedFile?.name),
    isZipPhpFile: false
  }

  const toggleFormValues = {
    displayMode: displayMode,
    feedbackMode: feedbackMode,
    gatedMode: enableSSO
      ? GATED_MODE_SSO
      : enableEmailWhitelist
        ? GATED_MODE_EMAIL_WHITELIST
        : enableEmailGate
          ? GATED_MODE_EMAIL_GATE
          : passwordProtected
            ? GATED_MODE_PASSWORD
            : GATED_MODE_STANDARD,
    enableEmailWhitelist,
    allowedEmails,
    passwordProtected,
    password: enableEmailGate || enableEmailWhitelist ? '' : password,
    disableIndexing: disableIndexing === 'true',
    disableExportPdf,
    siteConfiguration,
    errorPath: siteConfiguration ? JSON.parse(siteConfiguration).errorPath : '/',
    pdfPageAnalytics
  }

  const initialValues = {
    domainValues: domainFormValues,
    uploadValues: uploadFormValues,
    toggleValues: toggleFormValues
  }

  const validateForm = (values) => {
    const errors = validate(values)
    if (domainError) {
      errors.domainValues = errors.domainValues || {}
      errors.domainValues.subdomain = domainError
    }
    return errors
  }

  const shouldShowConvertToWebsiteButton = (uploadValues) =>
    action === ACTION_CREATE &&
    uploadValues.selectedFile &&
    isPDF(uploadValues.selectedFile) &&
    uploadValues.addedFiles?.length === 0 &&
    onConvertToWebsite

  return (
    <Formik
      initialValues={currentFormValues || initialValues}
      onSubmit={(values) => onSubmit(values, initialValues)}
      validate={validateForm}
      validateOnBlur={true}
      validateOnChange={true}
    >
      {({ values: { domainValues, uploadValues, toggleValues }, setFieldValue, isValid, errors }) => {
        return (
          <Form>
            {!inPreviewMode && (
              <DomainForm
                name={'domainValues'}
                initialValues={domainValues}
                userProfile={userProfile}
                action={action}
                disableProFeatures={disableProFeatures}
                setFieldValue={setFieldValue}
                uploadValues={uploadValues}
                updatingPhpFile={updatingPhpFile}
                setDomainError={setDomainError}
                domainError={domainError}
                disabled={feedbackMode}
              />
            )}

            {action === ACTION_CREATE && !uploadValues.selectedFile && (
              <div className="mt-2">
                <ActionBar
                  onOpenTextEditor={onOpenTextEditor}
                  onShowCodeEditor={onShowCodeEditor}
                  onOpenUrl={onOpenUrl}
                  onOpenGithubModal={onOpenGithubModal}
                  onOpenTransferRequestLinkModal={onOpenTransferRequestLinkModal}
                  onOpenDropBox={onOpenDropBox}
                  viewRepos={viewRepos}
                />
              </div>
            )}

            <FileSelectionForm
              name={'uploadValues'}
              initialValues={uploadValues}
              domainValues={domainValues}
              userProfile={userProfile}
              setFieldValue={setFieldValue}
              onShowCodeEditor={onShowCodeEditor}
            />

            {!updatingPhpFile && (
              <ToggleButtonsForm
                name={'toggleValues'}
                initialValues={toggleValues}
                userProfile={userProfile}
                uploadValues={uploadValues}
                setFieldValue={setFieldValue}
              />
            )}
            <Button
              className="action-btn submitButton"
              type="submit"
              disabled={domainError !== null || userProfile.status === ACCOUNT_STATUS.PAUSED}
              data-testid="CreateUpdateSiteForm-button-submit"
            >
              {LABEL_MAP[action]}
            </Button>

            {/* Convert to Website button - only show for PDFs in CREATE action with feature flag enabled */}
            {shouldShowConvertToWebsiteButton(uploadValues) && (
              <Button
                className="action-btn mt-2"
                type="button"
                variant="outline-primary"
                onClick={() => onConvertToWebsite(domainValues, uploadValues)}
                disabled={domainError !== null || userProfile.status === ACCOUNT_STATUS.PAUSED}
                data-testid="CreateUpdateSiteForm-button-convert-to-website"
              >
                Convert to Website
              </Button>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}

export default CreateUpdateSiteForm
